import Handlebars from 'handlebars';

/**
 * Template utility using Handlebars for rich template rendering.
 *
 * Supports:
 * - Variables: {{ variable }}, {{ nested.path }}
 * - Loops: {{#each items}}...{{/each}}
 * - Conditionals: {{#if condition}}...{{else}}...{{/if}}
 * - Inverted: {{#unless condition}}...{{/unless}}
 * - Comparison helpers: {{#if (eq a b)}}, {{#if (gt a b)}}, etc.
 * - Logical helpers: {{#if (and a b)}}, {{#if (or a b)}}, {{#if (not a)}}
 * - Date formatting: {{formatDate value 'yyyy/M/d'}} or {{formatDate value 'mediumDate'}}
 * - Number formatting: {{formatNumber num decimals}}, {{formatPercent num}}, {{formatCurrency num}}
 * - Unescaped HTML: {{{ html }}}
 * - Comments: {{! comment }}
 */

// Register comparison helpers
Handlebars.registerHelper('eq', (a: unknown, b: unknown) => a === b);
Handlebars.registerHelper('ne', (a: unknown, b: unknown) => a !== b);
Handlebars.registerHelper('gt', (a: number, b: number) => a > b);
Handlebars.registerHelper('gte', (a: number, b: number) => a >= b);
Handlebars.registerHelper('lt', (a: number, b: number) => a < b);
Handlebars.registerHelper('lte', (a: number, b: number) => a <= b);

// Register logical helpers
Handlebars.registerHelper('and', (...args: unknown[]) => {
  // Remove the Handlebars options object (last argument)
  const values = args.slice(0, -1);
  return values.every(Boolean);
});

Handlebars.registerHelper('or', (...args: unknown[]) => {
  // Remove the Handlebars options object (last argument)
  const values = args.slice(0, -1);
  return values.some(Boolean);
});

Handlebars.registerHelper('not', (value: unknown) => !value);

// Register string helpers
Handlebars.registerHelper('lowercase', (str: string) => (str ? String(str).toLowerCase() : ''));
Handlebars.registerHelper('uppercase', (str: string) => (str ? String(str).toUpperCase() : ''));
Handlebars.registerHelper('capitalize', (str: string) => {
  if (!str) return '';
  const s = String(str);
  return s.charAt(0).toUpperCase() + s.slice(1);
});
Handlebars.registerHelper('slice', (str: unknown, start: number, end?: unknown) => {
  if (str === undefined || str === null) return '';
  const s = String(str);
  return typeof end === 'number' ? s.slice(start, end) : s.slice(start);
});

// Register date formatting helper
// Supports Angular DatePipe named formats (mediumDate, shortDate, etc.) and
// pattern tokens: yyyy yy MM M dd d HH H hh h mm m ss s
// Usage: {{formatDate value}} or {{formatDate value 'yyyy/M/d'}} or {{formatDate value 'mediumDate'}}
Handlebars.registerHelper('formatDate', (value: unknown, format?: unknown) => {
  if (value == null || value === '') return '';

  const date = value instanceof Date ? value : new Date(value as string | number);
  if (isNaN(date.getTime())) return String(value);

  const fmt = typeof format === 'string' ? format : 'mediumDate';

  // Named format presets (mirrors Angular DatePipe named formats)
  const presets: Record<string, Intl.DateTimeFormatOptions> = {
    short: { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    shortDate: { year: 'numeric', month: 'numeric', day: 'numeric' },
    medium: { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' },
    mediumDate: { year: 'numeric', month: 'short', day: 'numeric' },
    long: {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    },
    longDate: { year: 'numeric', month: 'long', day: 'numeric' },
    full: {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'long',
    },
    fullDate: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    shortTime: { hour: 'numeric', minute: '2-digit' },
    mediumTime: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
  };

  if (presets[fmt]) {
    return new Intl.DateTimeFormat(undefined, presets[fmt]).format(date);
  }

  // Pattern-based format: tokenize left-to-right matching longest token first
  // Tokens: yyyy yy MM M dd d HH H hh h mm m ss s
  const tokens: Array<[string, () => string]> = [
    ['yyyy', () => date.getFullYear().toString()],
    ['yy', () => date.getFullYear().toString().slice(-2)],
    ['MM', () => String(date.getMonth() + 1).padStart(2, '0')],
    ['M', () => String(date.getMonth() + 1)],
    ['dd', () => String(date.getDate()).padStart(2, '0')],
    ['d', () => String(date.getDate())],
    ['HH', () => String(date.getHours()).padStart(2, '0')],
    ['H', () => String(date.getHours())],
    ['hh', () => String(date.getHours() % 12 || 12).padStart(2, '0')],
    ['h', () => String(date.getHours() % 12 || 12)],
    ['mm', () => String(date.getMinutes()).padStart(2, '0')],
    ['m', () => String(date.getMinutes())],
    ['ss', () => String(date.getSeconds()).padStart(2, '0')],
    ['s', () => String(date.getSeconds())],
  ];

  let result = '';
  let i = 0;
  while (i < fmt.length) {
    let matched = false;
    for (const [token, fn] of tokens) {
      if (fmt.startsWith(token, i)) {
        result += fn();
        i += token.length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      result += fmt[i];
      i++;
    }
  }
  return result;
});

// Register number/formatting helpers
Handlebars.registerHelper('formatNumber', (num: number, decimals?: number) => {
  if (typeof num !== 'number') return num;
  const d = typeof decimals === 'number' ? decimals : 0;
  return num.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
});

Handlebars.registerHelper('formatPercent', (num: number, decimals?: number) => {
  if (typeof num !== 'number') return num;
  const d = typeof decimals === 'number' ? decimals : 0;
  return num.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: d, maximumFractionDigits: d });
});

Handlebars.registerHelper('formatCurrency', (num: number, compact?: boolean) => {
  if (typeof num !== 'number') return num;
  if (compact) {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return '$' + (num / 1000).toFixed(0) + 'K';
  }
  return '$' + num.toLocaleString();
});

// Register array helpers
Handlebars.registerHelper('first', (array: unknown[]) => (Array.isArray(array) ? array[0] : undefined));
Handlebars.registerHelper('last', (array: unknown[]) => (Array.isArray(array) ? array[array.length - 1] : undefined));
Handlebars.registerHelper('length', (array: unknown[]) => (Array.isArray(array) ? array.length : 0));

// Register math helpers
Handlebars.registerHelper('add', (a: number, b: number) =>
  typeof a === 'number' && typeof b === 'number' ? a + b : 0,
);
Handlebars.registerHelper('subtract', (a: number, b: number) =>
  typeof a === 'number' && typeof b === 'number' ? a - b : 0,
);
Handlebars.registerHelper('multiply', (a: number, b: number) =>
  typeof a === 'number' && typeof b === 'number' ? a * b : 0,
);
Handlebars.registerHelper('divide', (a: number, b: number) =>
  typeof a === 'number' && typeof b === 'number' && b !== 0 ? a / b : 0,
);

// JSON serialization helper (for embedding data in data attributes)
Handlebars.registerHelper('json', (value: unknown) => {
  const str = JSON.stringify(value ?? null);
  return new Handlebars.SafeString(str.replace(/"/g, '&quot;'));
});

// Register conditional class/style helper
Handlebars.registerHelper('ifValue', (condition: unknown, trueValue: unknown, falseValue: unknown) => {
  return condition ? trueValue : falseValue;
});

// Register default/fallback helper - returns value if truthy, otherwise returns fallback
Handlebars.registerHelper('default', (value: unknown, fallback: unknown) => {
  return value !== undefined && value !== null && value !== '' ? value : fallback;
});

// Register array lookup helpers
// find - Find an item in an array where a property equals a value
// Usage: {{#with (find $context.datasource.products "value" $context.model.product)}}{{price}}{{/with}}
Handlebars.registerHelper('find', (array: unknown[], property: string, value: unknown) => {
  if (!Array.isArray(array)) return undefined;
  return array.find(
    (item) => item && typeof item === 'object' && (item as Record<string, unknown>)[property] === value,
  );
});

// get - Get a property from an object (useful for chaining with find)
// Usage: {{get (find $context.datasource.products "value" $context.model.product) "price"}}
Handlebars.registerHelper('get', (obj: unknown, property: string) => {
  if (!obj || typeof obj !== 'object') return undefined;
  return (obj as Record<string, unknown>)[property];
});

// Status color helper (common pattern)
Handlebars.registerHelper('statusColor', (status: string) => {
  const colors: Record<string, string> = {
    green: '#22c55e',
    amber: '#f59e0b',
    yellow: '#f59e0b',
    red: '#ef4444',
    blue: '#3b82f6',
    gray: '#9ca3af',
    grey: '#9ca3af',
  };
  return colors[String(status).toLowerCase()] || '#9ca3af';
});

// Cache for compiled templates
const templateCache = new Map<string, HandlebarsTemplateDelegate>();

/**
 * Render a Handlebars template with the given context.
 *
 * @param template - The Handlebars template string
 * @param context - The data context for rendering
 * @param options - Optional settings
 * @returns The rendered string
 *
 * @example
 * // Simple variable
 * renderTemplate('Hello {{ name }}!', { name: 'World' })
 * // => 'Hello World!'
 *
 * @example
 * // Loop with each
 * renderTemplate('{{#each items}}• {{ name }}\n{{/each}}', { items: [{name: 'A'}, {name: 'B'}] })
 * // => '• A\n• B\n'
 *
 * @example
 * // Conditional with eq helper
 * renderTemplate('{{#if (eq status "green")}}OK{{else}}Warning{{/if}}', { status: 'green' })
 * // => 'OK'
 *
 * @example
 * // Status color helper
 * renderTemplate('<span style="color: {{statusColor status}}">{{status}}</span>', { status: 'green' })
 * // => '<span style="color: #22c55e">green</span>'
 */
export function renderTemplate(
  template: string,
  context: Record<string, unknown>,
  options?: { cache?: boolean },
): string {
  const useCache = options?.cache ?? true;

  let compiledTemplate: HandlebarsTemplateDelegate;

  if (useCache && templateCache.has(template)) {
    compiledTemplate = templateCache.get(template)!;
  } else {
    compiledTemplate = Handlebars.compile(template);
    if (useCache) {
      templateCache.set(template, compiledTemplate);
    }
  }

  return compiledTemplate(context);
}

/**
 * Clear the template cache.
 * Useful for testing or when templates change dynamically.
 */
export function clearTemplateCache(): void {
  templateCache.clear();
}

/**
 * Register a custom Handlebars helper.
 *
 * @param name - The helper name
 * @param fn - The helper function
 *
 * @example
 * registerHelper('double', (num) => num * 2);
 * renderTemplate('{{ double value }}', { value: 5 }) // => '10'
 */
export function registerHelper(name: string, fn: Handlebars.HelperDelegate): void {
  Handlebars.registerHelper(name, fn);
}

/**
 * Register the `parse` helper, which bridges Handlebars templates to the platform
 * expression parser (ParserService) — the Handlebars analogue of the `parser` Angular pipe.
 *
 * The expression MUST be a quoted string literal (a bare `@uuid` collides with Handlebars'
 * own `@` data-variable sigil, and bare `$context.a.b` paths break Handlebars path parsing):
 *
 *   {{ parse "@uuid" }}
 *   {{ parse "$context.user.user_id" }}
 *   {{ parse "$context.page.params.id" }}
 *   {{{ parse "$context.user.bio" }}}   (triple-stache for unescaped HTML)
 *
 * The full ParserService function syntax (_concat, _toWords, _if, _switch, comparisons,
 * arithmetic, …) takes an object/array argument. Handlebars has no object literal, so write
 * the expression as a JSON string — single-quote the outer literal so the inner JSON keeps
 * its double quotes (no escaping needed). When the argument is a string that, trimmed, both
 * begins and ends with `{}`/`[]` and parses as JSON, it is decoded to a real object/array
 * before being handed to the parser; anything that is not valid JSON falls through unchanged:
 *
 *   {{ parse '{ "_concat": ["Hi, ", "$context.user.first_name"] }' }}
 *   {{ parse '{ "_toWords": "$context.model.status" }' }}
 *   {{ parse '{ "_if": [{ "_isEmpty": "$context.model.name" }, "Guest", "$context.model.name"] }' }}
 *
 * The current render root context is passed through as the parser's contextData so that
 * `$context.model.*` / `$context.datasource.*` resolve against the same data the rest of
 * the template sees. Generator/runtime expressions (`@uuid`, `@user_id`, `$context.user.*`)
 * are resolved by ParserService from injected services regardless of context.
 *
 * Note: generator expressions (`@uuid`, `@now`, `@timestamp`) recompute on every render —
 * they are display/runtime values, not the frozen-once value they are in config.
 *
 * `transform` is injected (rather than importing ParserService here) so this util stays
 * free of Angular DI. Wire it once at app bootstrap via APP_INITIALIZER.
 *
 * @param transform - Bridge to ParserService.transform(expr, contextData)
 */
export function registerParseHelper(transform: (expr: unknown, context: unknown) => unknown): void {
  Handlebars.registerHelper('parse', function (this: unknown, expr: unknown, options?: { data?: { root?: unknown } }) {
    const context = options?.data?.root ?? this ?? {};

    // Handlebars has no object literal, so object/array parser expressions arrive as a JSON
    // string. Decode them to the real object/array the parser expects; leave plain
    // expression strings (`@uuid`, `$context.*`) and non-JSON text untouched.
    let value: unknown = expr;
    if (typeof expr === 'string') {
      const trimmed = expr.trim();
      const looksLikeJson =
        (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
      if (looksLikeJson) {
        try {
          value = JSON.parse(trimmed);
        } catch {
          // Not valid JSON — treat as a literal/simple expression string.
        }
      }
    }

    return transform(value, context);
  });
}

/**
 * Get the Handlebars instance for advanced usage.
 */
export function getHandlebars(): typeof Handlebars {
  return Handlebars;
}
